import * as React from "react";
import * as locales from "../../../../locales.json";
import { I18n } from "i18n-js";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";
import styled from "styled-components";
import { datadogRum } from "@datadog/browser-rum";

import * as API from "../../../../api/billing";
import Modal from "../../../reusable/reactModal";
import { SubscriptionCreditCardModalPropTypes } from "../lib/types";
import {
  StyledButton,
  StyledButtonContainer,
  StyledModalBody,
  StyledModalContents,
  StyledModalText,
} from "../lib/styles";
import { getCardDisplayName } from "./creditCardPaymentMethod";
import { redesignTheme } from "../../../theme/theme";

const StyledDescriptionBold = styled(StyledModalText)`
  font-weight: ${redesignTheme.fontWeight.semibold};
`;

const SubscriptionCreditCardModal = ({
  cardType,
  last4,
  closeModal,
  fetchData,
  modal,
  openPortal,
  paymentMethodOnFile,
  setPageLoading,
  subscriptionId,
  updateToastMessage,
}: SubscriptionCreditCardModalPropTypes): React.ReactElement => {
  const enableAutoCollect = async (): Promise<void> => {
    setPageLoading(true);

    try {
      await API.postEnableAutoCollect(subscriptionId);

      updateToastMessage({
        isOpen: true,
        message: i18n.translate(
          "admin_pages.settings.billing.subscription_cc_modal.success"
        ),
        type: "success",
      });
      fetchData();
    } catch (error) {
      datadogRum.addError(error);
      updateToastMessage({
        isOpen: true,
        message: i18n.translate(
          "admin_pages.settings.billing.subscription_cc_modal.error"
        ),
        type: "error",
      });
    } finally {
      closeModal();
      setPageLoading(false);
    }
  };

  const creditCardOnFile = async (): Promise<boolean> => {
    try {
      const response = await API.getCompanyBillingPage();
      return response.data.data.paymentMethodOnFile;
    } catch (error) {
      datadogRum.addError(error);
      return false;
    }
  };

  const openPortalCallback = async (): Promise<void> => {
    setPageLoading(true);
    const hasCreditCardOnFile = await creditCardOnFile();

    if (hasCreditCardOnFile) {
      enableAutoCollect();
    } else {
      updateToastMessage({
        isOpen: true,
        message: i18n.translate(
          "admin_pages.settings.billing.subscription_cc_modal.error_no_cc"
        ),
        type: "error",
      });
      setPageLoading(false);
    }
  };

  const handleAddCreditCardClick = (): void => {
    window.amplitude?.track("Add Credit Card Modal Opened");

    closeModal();
    openPortal(null, "PAYMENT_SOURCES", openPortalCallback);
  };

  return (
    <Modal
      ariaLabel={i18n.translate(
        "admin_pages.settings.billing.subscription_cc_modal.aria_label"
      )}
      close={closeModal}
      showDialog={modal === "subscriptionCreditCard"}
      testId="subscription-credit-card-modal"
      title={`${i18n.translate(
        "admin_pages.settings.billing.subscription_cc_modal.title"
      )}`}
    >
      <StyledModalBody>
        <StyledModalContents>
          <StyledModalText>
            {i18n.translate(
              "admin_pages.settings.billing.subscription_cc_modal.description"
            )}
          </StyledModalText>
          <br />
          <StyledDescriptionBold>
            {paymentMethodOnFile
              ? i18n.translate(
                  "admin_pages.settings.billing.subscription_cc_modal.description_cc",
                  { cardType: getCardDisplayName(cardType), last4 }
                )
              : i18n.translate(
                  "admin_pages.settings.billing.subscription_cc_modal.description_no_cc"
                )}
          </StyledDescriptionBold>
        </StyledModalContents>
        <StyledButtonContainer>
          <StyledButton
            onClick={closeModal}
            size="small"
            testId="cancel-button"
            variant="secondary"
          >
            {i18n.translate("forms.cancel")}
          </StyledButton>
          <StyledButton
            onClick={
              paymentMethodOnFile ? enableAutoCollect : handleAddCreditCardClick
            }
            size="small"
            testId={
              paymentMethodOnFile ? "confirm-button" : "add-credit-card-button"
            }
            variant="primary"
          >
            {paymentMethodOnFile
              ? i18n.translate(
                  "admin_pages.settings.billing.subscription_cc_modal.confirm"
                )
              : i18n.translate(
                  "admin_pages.settings.billing.subscription_cc_modal.add_credit_card"
                )}
          </StyledButton>
        </StyledButtonContainer>
      </StyledModalBody>
    </Modal>
  );
};

export default SubscriptionCreditCardModal;
